import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/hooks/useAuth';
import LevelSettings from './components/LevelSettings';

export default function Ayarlar() {
  const { profile, user } = useAuth();
  const [searchParams] = useSearchParams();
  const passwordSectionRef = useRef<HTMLElement>(null);
  const [tab, setTab] = useState<'hesap' | 'seviye' | 'deneme'>(() => {
    const p = searchParams.get('tab');
    if (p === 'seviye') return 'seviye';
    if (p === 'deneme') return 'deneme';
    return 'hesap';
  });

  // Profil formu
  const [fullName, setFullName] = useState('');
  const [phone, setPhone] = useState('');
  const [savingProfile, setSavingProfile] = useState(false);
  const [profileMsg, setProfileMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Şifre formu
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [savingPassword, setSavingPassword] = useState(false);
  const [passwordMsg, setPasswordMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Deneme dersi (misafir girişi) ayarları
  const [denemeBaslik, setDenemeBaslik] = useState('');
  const [denemeAciklama, setDenemeAciklama] = useState('');
  const [denemeTarih, setDenemeTarih] = useState('');
  const [denemeSaat, setDenemeSaat] = useState('');
  const [denemeLink, setDenemeLink] = useState('');
  const [savingDeneme, setSavingDeneme] = useState(false);
  const [denemeMsg, setDenemeMsg] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (profile) {
      setFullName(profile.full_name ?? '');
      setPhone(profile.phone ?? '');
    }
  }, [profile]);

  useEffect(() => {
    if (searchParams.get('tab') === 'sifre') {
      setTab('hesap');
      const t = setTimeout(() => {
        passwordSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 100);
      return () => clearTimeout(t);
    }
  }, [searchParams]);

  useEffect(() => {
    let cancelled = false;
    supabase
      .from('zehrahoca_settings')
      .select('*')
      .eq('id', 1)
      .maybeSingle()
      .then(({ data }) => {
        if (!cancelled && data) {
          setDenemeBaslik(data.deneme_dersi_baslik ?? '');
          setDenemeAciklama(data.deneme_dersi_aciklama ?? '');
          setDenemeTarih(data.deneme_dersi_tarih ?? '');
          setDenemeSaat(data.deneme_dersi_saat ?? '');
          setDenemeLink(data.deneme_dersi_link ?? '');
        }
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, []);

  async function handleSaveProfile() {
    if (!profile) return;
    setSavingProfile(true);
    setProfileMsg(null);
    try {
      const { error } = await supabase
        .from('zehrahoca_profiles')
        .update({ full_name: fullName.trim(), phone: phone.trim() })
        .eq('id', profile.id);

      if (error) throw error;
      setProfileMsg({ type: 'success', text: 'Profil bilgileriniz güncellendi.' });
    } catch (err) {
      setProfileMsg({ type: 'error', text: (err as Error).message || 'Profil güncellenirken bir hata oluştu.' });
    } finally {
      setSavingProfile(false);
    }
  }

  async function handleChangePassword() {
    setSavingPassword(true);
    setPasswordMsg(null);
    try {
      if (newPassword.length < 6) {
        setPasswordMsg({ type: 'error', text: 'Yeni şifre en az 6 karakter olmalı.' });
        return;
      }
      if (newPassword !== confirmPassword) {
        setPasswordMsg({ type: 'error', text: 'Şifreler birbiriyle eşleşmiyor.' });
        return;
      }

      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;

      setNewPassword('');
      setConfirmPassword('');
      setPasswordMsg({ type: 'success', text: 'Şifreniz başarıyla değiştirildi.' });
    } catch (err) {
      setPasswordMsg({ type: 'error', text: (err as Error).message || 'Şifre değiştirilirken bir hata oluştu.' });
    } finally {
      setSavingPassword(false);
    }
  }

  async function handleSaveDenemeDersi() {
    setSavingDeneme(true);
    setDenemeMsg(null);
    try {
      const { error } = await supabase.from('zehrahoca_settings').upsert({
        id: 1,
        deneme_dersi_baslik: denemeBaslik.trim(),
        deneme_dersi_aciklama: denemeAciklama.trim(),
        deneme_dersi_tarih: denemeTarih,
        deneme_dersi_saat: denemeSaat,
        deneme_dersi_link: denemeLink.trim(),
        updated_at: new Date().toISOString(),
      });
      if (error) throw error;
      setDenemeMsg({ type: 'success', text: 'Deneme dersi bilgileri kaydedildi ve misafir sayfasında yayında.' });
    } catch (err) {
      setDenemeMsg({ type: 'error', text: (err as Error).message || 'Deneme dersi kaydedilirken bir hata oluştu.' });
    } finally {
      setSavingDeneme(false);
    }
  }

  const registerDate = profile?.created_at
    ? new Date(profile.created_at).toLocaleDateString('tr-TR', { day: 'numeric', month: 'long', year: 'numeric' })
    : '—';

  return (
    <div className="mx-auto max-w-4xl space-y-6">
      <div>
        <h1 className="font-heading text-2xl font-bold text-foreground-950 md:text-3xl">Ayarlar</h1>
        <p className="mt-1 text-sm text-foreground-600">Hesap, seviye ve ders ayarlarınızı buradan yönetin.</p>
      </div>

      <div className="grid grid-cols-3 gap-1 rounded-full bg-background-100 p-1">
        {([
          { key: 'hesap', label: 'Hesap' },
          { key: 'seviye', label: 'Seviye Ayarları' },
          { key: 'deneme', label: 'Deneme Dersi' },
        ] as const).map((t) => (
          <button
            key={t.key}
            type="button"
            onClick={() => setTab(t.key)}
            className={`whitespace-nowrap rounded-full px-4 py-2.5 text-sm font-semibold transition cursor-pointer ${
              tab === t.key ? 'bg-white text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-800'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'seviye' && <LevelSettings />}

      {tab === 'hesap' && (
        <>
          {/* Profil Bilgileri */}
          <section className="rounded-2xl border border-background-200 bg-white p-6">
            <div className="mb-5 flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary-100 text-primary-700">
                <i className="ri-user-settings-line text-lg" />
              </span>
              <div>
                <h2 className="font-heading text-base font-bold text-foreground-950">Profil Bilgileri</h2>
                <p className="text-xs text-foreground-500">Panelde görünen ad ve iletişim bilgileriniz.</p>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="ayar-ad" className="mb-1.5 block text-sm font-medium text-foreground-700">
                  Ad Soyad
                </label>
                <input
                  id="ayar-ad"
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e.target.value)}
                  className="w-full rounded-lg border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                  placeholder="Adınız ve soyadınız"
                />
              </div>
              <div>
                <label htmlFor="ayar-tel" className="mb-1.5 block text-sm font-medium text-foreground-700">
                  Telefon
                </label>
                <input
                  id="ayar-tel"
                  type="tel"
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  className="w-full rounded-lg border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                  placeholder="05xx xxx xx xx"
                />
              </div>
              <div className="sm:col-span-2">
                <label htmlFor="ayar-email" className="mb-1.5 block text-sm font-medium text-foreground-700">
                  E-posta
                </label>
                <input
                  id="ayar-email"
                  type="email"
                  value={user?.email ?? profile?.email ?? ''}
                  disabled
                  className="w-full cursor-not-allowed rounded-lg border border-background-300 bg-background-100 px-3.5 py-2.5 text-sm text-foreground-500"
                />
                <p className="mt-1 text-xs text-foreground-400">Giriş e-postanız değiştirilemez.</p>
              </div>
            </div>

            {profileMsg && (
              <div
                className={`mt-4 rounded-lg px-4 py-2.5 text-sm ${
                  profileMsg.type === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
                }`}
              >
                {profileMsg.text}
              </div>
            )}

            <div className="mt-5 flex justify-end">
              <button
                type="button"
                onClick={handleSaveProfile}
                disabled={savingProfile}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-primary-600 disabled:opacity-70 cursor-pointer"
              >
                {savingProfile ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-save-line" />}
                {savingProfile ? 'Kaydediliyor...' : 'Değişiklikleri Kaydet'}
              </button>
            </div>
          </section>

          {/* Şifre Değiştir */}
          <section
            ref={(el) => passwordSectionRef.current = el}
            className="rounded-2xl border border-background-200 bg-white p-6"
          >
            <div className="mb-5 flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent-100 text-accent-800">
                <i className="ri-lock-password-line text-lg" />
              </span>
              <div>
                <h2 className="font-heading text-base font-bold text-foreground-950">Şifre Değiştir</h2>
                <p className="text-xs text-foreground-500">Güçlü bir şifre seçmeniz hesabınızı daha güvenli kılar.</p>
              </div>
            </div>

            <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
              <div>
                <label htmlFor="ayar-yeni-sifre" className="mb-1.5 block text-sm font-medium text-foreground-700">
                  Yeni Şifre
                </label>
                <input
                  id="ayar-yeni-sifre"
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full rounded-lg border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                  placeholder="En az 6 karakter"
                />
              </div>
              <div>
                <label htmlFor="ayar-sifre-tekrar" className="mb-1.5 block text-sm font-medium text-foreground-700">
                  Yeni Şifre (Tekrar)
                </label>
                <input
                  id="ayar-sifre-tekrar"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full rounded-lg border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                  placeholder="Şifreyi tekrar girin"
                />
              </div>
            </div>

            {passwordMsg && (
              <div
                className={`mt-4 rounded-lg px-4 py-2.5 text-sm ${
                  passwordMsg.type === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
                }`}
              >
                {passwordMsg.text}
              </div>
            )}

            <div className="mt-5 flex justify-end">
              <button
                type="button"
                onClick={handleChangePassword}
                disabled={savingPassword}
                className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-accent-500 px-5 py-2.5 text-sm font-semibold text-primary-950 transition hover:bg-accent-600 disabled:opacity-70 cursor-pointer"
              >
                {savingPassword ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-key-2-line" />}
                {savingPassword ? 'Değiştiriliyor...' : 'Şifreyi Güncelle'}
              </button>
            </div>
          </section>

          {/* Hesap Bilgileri */}
          <section className="rounded-2xl border border-background-200 bg-background-50 p-6">
            <div className="mb-4 flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-secondary-100 text-secondary-700">
                <i className="ri-information-line text-lg" />
              </span>
              <h2 className="font-heading text-base font-bold text-foreground-950">Hesap Bilgileri</h2>
            </div>
            <dl className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div>
                <dt className="text-xs font-semibold text-foreground-500">Rol</dt>
                <dd className="mt-0.5 text-sm font-medium capitalize text-foreground-900">
                  {profile?.role === 'ogretmen' ? 'Öğretmen / Yönetici' : 'Öğrenci'}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-semibold text-foreground-500">Kayıt Tarihi</dt>
                <dd className="mt-0.5 text-sm font-medium text-foreground-900">{registerDate}</dd>
              </div>
              <div>
                <dt className="text-xs font-semibold text-foreground-500">Hesap Durumu</dt>
                <dd className="mt-0.5 flex items-center gap-1.5 text-sm font-medium text-emerald-600">
                  <span className="h-2 w-2 rounded-full bg-emerald-500" /> Aktif
                </dd>
              </div>
            </dl>
          </section>
        </>
      )}

      {tab === 'deneme' && (
        <section className="rounded-2xl border border-background-200 bg-white p-6">
          <div className="mb-5 flex items-center gap-3">
            <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-accent-100 text-accent-800">
              <i className="ri-vidicon-line text-lg" />
            </span>
            <div>
              <h2 className="font-heading text-base font-bold text-foreground-950">Deneme Dersi (Misafir Girişi)</h2>
              <p className="text-xs text-foreground-500">Kayıtsız misafirlerin görebileceği deneme dersinin bilgileri.</p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <label htmlFor="deneme-baslik" className="mb-1.5 block text-sm font-medium text-foreground-700">
                Ders Başlığı
              </label>
              <input
                id="deneme-baslik"
                type="text"
                value={denemeBaslik}
                onChange={(e) => setDenemeBaslik(e.target.value)}
                className="w-full rounded-lg border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                placeholder="Örn: Ücretsiz Tanışma & Seviye Tespit Dersi"
              />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="deneme-aciklama" className="mb-1.5 block text-sm font-medium text-foreground-700">
                Açıklama
              </label>
              <textarea
                id="deneme-aciklama"
                value={denemeAciklama}
                onChange={(e) => setDenemeAciklama(e.target.value)}
                rows={3}
                className="w-full resize-none rounded-lg border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                placeholder="Derste neler işleneceğine dair kısa bir açıklama"
              />
            </div>
            <div>
              <label htmlFor="deneme-tarih" className="mb-1.5 block text-sm font-medium text-foreground-700">
                Tarih
              </label>
              <input
                id="deneme-tarih"
                type="date"
                value={denemeTarih}
                onChange={(e) => setDenemeTarih(e.target.value)}
                className="w-full rounded-lg border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100 cursor-pointer"
              />
            </div>
            <div>
              <label htmlFor="deneme-saat" className="mb-1.5 block text-sm font-medium text-foreground-700">
                Saat
              </label>
              <input
                id="deneme-saat"
                type="time"
                value={denemeSaat}
                onChange={(e) => setDenemeSaat(e.target.value)}
                className="w-full rounded-lg border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100 cursor-pointer"
              />
            </div>
            <div className="sm:col-span-2">
              <label htmlFor="deneme-link" className="mb-1.5 block text-sm font-medium text-foreground-700">
                Canlı Ders Linki (Zoom / Meet)
              </label>
              <input
                id="deneme-link"
                type="url"
                value={denemeLink}
                onChange={(e) => setDenemeLink(e.target.value)}
                className="w-full rounded-lg border border-background-300 bg-background-50 px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
                placeholder="https://meet.google.com/xxx-xxxx-xxx"
              />
            </div>
          </div>

          {denemeMsg && (
            <div
              className={`mt-4 rounded-lg px-4 py-2.5 text-sm ${
                denemeMsg.type === 'success' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-700'
              }`}
            >
              {denemeMsg.text}
            </div>
          )}

          <div className="mt-5 flex flex-col items-start justify-between gap-3 sm:flex-row sm:items-center">
            <p className="text-xs text-foreground-500">
              <i className="ri-information-line mr-1" />
              Bu bilgiler misafir girişindeki deneme dersi sayfasında görünür.
            </p>
            <button
              type="button"
              onClick={handleSaveDenemeDersi}
              disabled={savingDeneme}
              className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-accent-500 px-5 py-2.5 text-sm font-semibold text-primary-950 transition hover:bg-accent-600 disabled:opacity-70 cursor-pointer"
            >
              {savingDeneme ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-save-line" />}
              {savingDeneme ? 'Kaydediliyor...' : 'Deneme Dersini Kaydet'}
            </button>
          </div>
        </section>
      )}
    </div>
  );
}